import {
  useColorMode,
  GridItem,
  AbsoluteCenter,
  Fade,
  Spinner,
  Text,
  useColorModeValue,
  Box,
  Heading,
  Image,
} from '@chakra-ui/react';
import {
  createContext,
  Dispatch,
  MutableRefObject,
  ReactElement,
  SetStateAction,
  useCallback,
  useContext,
  useState,
  useEffect,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'urql';
import { useLocation, useNavigate } from 'react-router-dom';
import { faEyeSlash, faMousePointer } from '@fortawesome/free-solid-svg-icons';
import invariant from 'tiny-invariant';
import { ReactComponent as IsolationIcon } from '../../../assets/icons/isolation.svg';
import SparkelIcon from '../../../components/common/icon/SparkelIcon';
import noSheetsPlaceholder from '../../../assets/img/no-sheets-placeholder.svg';
import PlaceholderText from '../../text/PlaceholderText';
import { usePdfOpacity } from './hooks/usePdfOpacity';
import { useViewerMode, ViewerMode } from 'src/components/common/viewer-mode';
import { useEnabledSheetViewerModeValue } from 'src/components/viewers/common/hooks/useEnabledSheetViewerModeValue';
import {
  SelectedDbIds,
  useSelectedDbIds,
} from 'src/components/viewers/common/hooks/useSelectedDbIds';
import { SheetShapesManager } from 'src/components/viewers/common/hooks/useSheetShapesManager';
import {
  SheetsVisibilityManager,
  useSheetsVisibility,
} from 'src/components/viewers/common/hooks/useSheetsVisibilityManager';
import { ActiveTool } from 'src/components/viewers/SheetsViewerV2/enums/ActiveTool';
import {
  ActiveSheet,
  useActiveSheet,
} from 'src/components/viewers/SheetsViewerV2/hooks/useActiveSheet';
import { ActiveSheetPagination } from 'src/components/viewers/SheetsViewerV2/hooks/useActiveSheetPagination';
import {
  SheetViewerV2Calibration,
  useCalibration,
} from 'src/components/viewers/SheetsViewerV2/hooks/useCalibration';
import { useMeasurementTool } from 'src/components/viewers/SheetsViewerV2/hooks/useMeasurementTool';
import { useShapes } from 'src/components/viewers/SheetsViewerV2/hooks/useShapes';
import { useSheetsAggregatedView } from 'src/components/viewers/SheetsViewerV2/hooks/useSheetsAggregatedView';
import { Sheet } from 'src/hooks/sheets';
import { useShapesDrawingTool } from 'src/components/viewers/SheetsViewerV2/hooks/useShapesDrawingTool';
import {
  ContextMenuContextType,
  ContextMenuProvider,
  OnShowProps,
  PredicateParams,
  useCustomContextMenu,
} from 'src/services/viewer/CustomContextMenu';
import {
  DbIdsPerModel,
  SheetShapesMap,
} from 'src/components/common/ForgeViewer';
import {
  isShapeHidden,
  shapeDbIdDataAttribute,
  shapeUrnDataAttribute,
} from 'src/components/SheetShapes';
import AggregatedView = Autodesk.Viewing.AggregatedView;
import { setTheme } from 'src/components/viewers/SheetsViewerV2/utils/theme';
import { useLocalStorage } from 'src/hooks/local-storage';
import {
  SheetsColorizationManager,
  useSheetsColorization,
} from 'src/components/viewers/common/hooks/useSheetsColorizationManager';
import {
  GetSparkelPropertiesForProjectDocument,
  GetSparkelPropertiesForProjectQuery,
} from 'src/gql/graphql';
import { DrawingMode } from 'src/components/viewers/SheetsViewerV2/autodesk/tools/ShapesDrawingTool';

export type SheetsViewerV2ContextType = SelectedDbIds & {
  projectId: string;
  viewer: {
    elementRef: MutableRefObject<HTMLDivElement | null>;
    aggregated: AggregatedView | null;
  };
  viewerMode: ViewerMode;
  activeSheet: ActiveSheet | null;
  activeSheetId: string | null;
  changeActiveSheet: (sheetId: string | null) => void;
  sheets: Sheet[];
  pagination: ActiveSheetPagination;
  setPageNumber: (pageNumber: number) => void;
  calibration: SheetViewerV2Calibration;
  shapesManager: SheetShapesManager;
  visibilityManager: SheetsVisibilityManager;
  activeTool: ActiveTool;
  setActiveTool: Dispatch<SetStateAction<ActiveTool>>;
  measurement: { isMeasuring: boolean };
  drawing: ReturnType<typeof useShapesDrawingTool>;
  pdfGhosting: {
    opacity: number;
    setOpacity: (opacity: number) => void;
  };
  showSheetShapeLabels: boolean;
  setShowSheetShapeLabels: (value: boolean) => void;
  colorization: SheetsColorizationManager;
  propertyData: GetSparkelPropertiesForProjectQuery | undefined;
  isLoading: boolean;
};

export const SheetsViewerV2Context =
  createContext<SheetsViewerV2ContextType | null>(null);

type Props = {
  projectId: string;
  shouldHideViewer: boolean;
  children: ReactElement;
};

// Sheet-specific context menu types
export type SheetViewerV2MenuProps = {
  selections: DbIdsPerModel;
  hasSelectedElements: boolean;
  target?: {
    type: 'SHAPE';
    modelUrn: string;
    dbId: number;
  };
};

export const SheetViewerV2ContextMenuContext = createContext<
  ContextMenuContextType<SheetViewerV2MenuProps>
>({
  registerItem: () => {
    throw new Error('No Context Menu Context provider found.');
  },
  unregisterItem: () => {
    throw new Error('No Context Menu Context provider found.');
  },
  items: [],
});

export const SheetViewerV2ContextMenuProvider = ({
  children,
}: {
  children: React.ReactElement;
}) => {
  return (
    <ContextMenuProvider context={SheetViewerV2ContextMenuContext}>
      {children}
    </ContextMenuProvider>
  );
};

// Hook to create a context menu for SheetViewerV2 that works with sheet shapes
function useSheetViewerV2ContextMenu(
  viewerRef: React.RefObject<HTMLDivElement>,
  visibilityManager: SheetsVisibilityManager,
  selectedDbIds: DbIdsPerModel,
  setSelectedDbIds: (dbIds: DbIdsPerModel) => void,
  renderedSheetShapes: SheetShapesMap,
  activeTool?: ActiveTool
) {
  const showAndSelect = useCallback(
    async ({ event, showContextMenu }: OnShowProps<SheetViewerV2MenuProps>) => {
      const currentViewer = viewerRef.current;
      if (!currentViewer) {
        return;
      }

      // Don't show context menu when in drawing mode (right-click is used to close polygons)
      if (activeTool === ActiveTool.Drawing) {
        return;
      }

      // Get mouse coordinates
      let clientX: number;
      let clientY: number;

      if (event.type === 'mouseup') {
        event = event as React.MouseEvent<HTMLDivElement>;
        clientX = event.clientX;
        clientY = event.clientY;
      } else {
        invariant(event.type === 'touchstart', 'Unexpected event type');
        const touch = (event as React.TouchEvent<HTMLDivElement>).touches[0];
        clientX = touch.clientX;
        clientY = touch.clientY;
      }

      const currentSelection = Object.values(selectedDbIds).flat();

      // Use DOM-based selection like the original SheetsContextMenu
      const clickedElement = document.elementFromPoint(clientX, clientY);
      // read dbId and urn from clickedElement, if present
      const dbIdString = clickedElement?.getAttribute(shapeDbIdDataAttribute);
      const urn = clickedElement?.getAttribute(shapeUrnDataAttribute);

      if (dbIdString && urn && currentSelection.length === 0) {
        const dbId = Number(dbIdString);
        setSelectedDbIds({
          [urn]: [Number(dbId)],
        });
        showContextMenu({
          event,
          props: {
            selections: {
              [urn]: [dbId],
            },
            hasSelectedElements: true,
            target: {
              type: 'SHAPE',
              dbId,
              modelUrn: urn,
            },
          },
        });
      } else if (currentSelection.length === 1) {
        showContextMenu({
          event,
          props: {
            hasSelectedElements: true,
            selections: selectedDbIds,
            target: {
              type: 'SHAPE',
              dbId: currentSelection[0],
              modelUrn: Object.keys(selectedDbIds)[0],
            },
          },
        });
      } else if (currentSelection.length > 1) {
        showContextMenu({
          event,
          props: {
            hasSelectedElements: true,
            selections: selectedDbIds,
          },
        });
      } else {
        showContextMenu({ event });
      }
    },
    [selectedDbIds, setSelectedDbIds, viewerRef, activeTool]
  );

  const { items } = useSheetViewerV2ContextMenuContext();

  return useCustomContextMenu(showAndSelect, items, [
    'viewer visibility',
    'viewer selection',
  ]);
}

export const useSheetViewerV2ContextMenuContext = () =>
  useContext(SheetViewerV2ContextMenuContext);

// eslint-disable-next-line complexity
export function SheetsViewerV2ContextProvider(props: Props) {
  const [activeTool, setActiveToolState] = useState(ActiveTool.Default);
  const setActiveTool = useCallback<Dispatch<SetStateAction<ActiveTool>>>(
    (value) => {
      setActiveToolState(value);
    },
    []
  );
  const activeToolContext = { activeTool, setActiveTool };
  const { projectId, children, shouldHideViewer } = props;
  const { viewerMode } = useViewerMode();
  const { sheetsViewerMode } = useEnabledSheetViewerModeValue();
  const { viewerRef, aggregatedView } = useSheetsAggregatedView();
  const { colorMode } = useColorMode();
  const location = useLocation();
  const navigate = useNavigate();
  const activeSheetContext = useActiveSheet({ projectId, aggregatedView });
  const pdfGhosting = usePdfOpacity({
    aggregatedView,
    viewerMode,
    sheetId: activeSheetContext.activeSheetId,
    pageNumber: activeSheetContext.pagination.pageNumber,
  });
  const calibration = useCalibration({
    aggregatedView,
    ...activeToolContext,
    ...activeSheetContext,
  });
  const visibilityManager = useSheetsVisibility();
  const selectDbIds = useSelectedDbIds();
  const [showSheetShapeLabels, setShowSheetShapeLabels] = useLocalStorage(
    'showSheetShapeLabels',
    true
  );

  const colorization = useSheetsColorization();

  const [{ data: sparkelPropertiesQueryData }] = useQuery({
    query: GetSparkelPropertiesForProjectDocument,
    variables: { projectId },
  });

  // Use a stable reference with useMemo - same pattern as V1
  const propertyData = useMemo(
    () => sparkelPropertiesQueryData,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(sparkelPropertiesQueryData)]
  );

  const measurement = useMeasurementTool({
    activeTool,
    viewer: aggregatedView?.viewer,
    calibration: calibration.existing,
    activeSheetData: activeSheetContext.activeSheet?.data || null,
  });

  const { shapesManager } = useShapes(
    aggregatedView?.viewer,
    activeTool,
    activeSheetContext.activeSheet,
    selectDbIds,
    visibilityManager,
    showSheetShapeLabels,
    colorization,
    calibration,
    propertyData,
    calibration.existing || undefined
  );

  const drawing = useShapesDrawingTool({
    activeTool,
    projectId,
    viewer: aggregatedView?.viewer,
    activeSheetData: activeSheetContext.activeSheet?.data || null,
    calibration: calibration.existing,
    renderedSheetShapes: shapesManager.renderedSheetShapes,
    propertyData,
  });

  // Create context menu for SheetViewerV2 with sheet shapes
  const { menuElement, bind: bindContextMenu } = useSheetViewerV2ContextMenu(
    viewerRef,
    visibilityManager,
    selectDbIds.selectedDbIds,
    selectDbIds.setSelectedDbIds,
    shapesManager.renderedSheetShapes,
    activeTool
  );

  // Register sheet-specific context menu items
  const { registerItem, unregisterItem } = useSheetViewerV2ContextMenuContext();
  const { t } = useTranslation('project');

  // Determine loading state - show loading when viewer is initializing or sheet is loading
  const isLoading =
    !aggregatedView || activeSheetContext.activeSheet?.isLoading;

  const selectAllVisibleHandler = useCallback(() => {
    const visibleShapes = Object.fromEntries(
      Object.keys(shapesManager.renderedSheetShapes).map((urn) => [
        urn,
        shapesManager.renderedSheetShapes[urn]
          .filter(
            (shape) =>
              !isShapeHidden(
                visibilityManager.hiddenElements,
                urn,
                shape,
                visibilityManager.hiddenSheetShapes,
                visibilityManager.isolatedElements,
                false
              )
          )
          .map((shape) => shape.dbId),
      ])
    );
    selectDbIds.setSelectedDbIds(visibleShapes);
  }, [
    shapesManager.renderedSheetShapes,
    selectDbIds.setSelectedDbIds,
    visibilityManager.hiddenElements,
    visibilityManager.hiddenSheetShapes,
    visibilityManager.isolatedElements,
  ]);

  const hideSelectedHandler = useCallback(() => {
    visibilityManager.hide(selectDbIds.selectedDbIds);
    selectDbIds.setSelectedDbIds({});
  }, [
    selectDbIds.selectedDbIds,
    selectDbIds.setSelectedDbIds,
    visibilityManager,
  ]);

  const isolateSelectedHandler = useCallback(() => {
    visibilityManager.setIsolatedElements(selectDbIds.selectedDbIds);
  }, [selectDbIds.selectedDbIds, visibilityManager]);

  const hideSelectionRelatedItems = useCallback(
    ({ props }: PredicateParams<SheetViewerV2MenuProps>) => {
      if (!props) {
        return true;
      } else {
        return !props.hasSelectedElements;
      }
    },
    []
  );

  // Register context menu items
  useEffect(() => {
    registerItem({
      id: 'isolateSelected',
      label: t('viewer-menu.isolate-selected'),
      onClick: isolateSelectedHandler,
      hidden: hideSelectionRelatedItems,
      group: 'viewer visibility',
      icon: <SparkelIcon as={IsolationIcon} />,
    });
    return () => {
      unregisterItem('isolateSelected');
    };
  }, [
    hideSelectionRelatedItems,
    registerItem,
    isolateSelectedHandler,
    unregisterItem,
    t,
  ]);

  useEffect(() => {
    registerItem({
      id: 'hideSelected',
      label: t('viewer-menu.hide-selected'),
      onClick: hideSelectedHandler,
      hidden: hideSelectionRelatedItems,
      group: 'viewer visibility',
      icon: <SparkelIcon size="sm" fixedWidth icon={faEyeSlash} />,
    });
    return () => {
      unregisterItem('hideSelected');
    };
  }, [
    hideSelectionRelatedItems,
    registerItem,
    hideSelectedHandler,
    unregisterItem,
    t,
  ]);

  useEffect(() => {
    registerItem({
      id: 'selectAllVisible',
      label: t('viewer-menu.select-visible'),
      onClick: selectAllVisibleHandler,
      group: 'viewer selection',
      icon: <SparkelIcon size="sm" fixedWidth icon={faMousePointer} />,
    });
    return () => {
      unregisterItem('selectAllVisible');
    };
  }, [registerItem, selectAllVisibleHandler, t, unregisterItem]);

  // Apply theme when color mode changes
  useEffect(() => {
    if (aggregatedView && viewerMode === ViewerMode.SheetsV2) {
      setTheme(aggregatedView, { colorMode });
    }
  }, [aggregatedView, colorMode, viewerMode]);

  // One-time auto-activation of drawing mode based on URL param
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const autoDraw = params.get('autoDraw');
    if (
      viewerMode === ViewerMode.SheetsV2 &&
      activeSheetContext.activeSheet?.data &&
      autoDraw &&
      activeTool === ActiveTool.Default
    ) {
      if (autoDraw === 'magicPolygon') {
        setActiveTool(ActiveTool.Drawing);
        drawing.setDrawingMode(DrawingMode.MagicPolygon);
      }
      params.delete('autoDraw');
      navigate({ search: params.toString() }, { replace: true });
    }
  }, [
    location.search,
    viewerMode,
    activeSheetContext.activeSheet,
    setActiveTool,
    drawing,
    navigate,
    activeTool,
  ]);

  const loadingTextColor = useColorModeValue('gray.500', 'gray.300');

  return (
    <SheetsViewerV2Context.Provider
      value={{
        viewer: {
          elementRef: viewerRef,
          aggregated: aggregatedView,
        },
        projectId,
        viewerMode,
        calibration,
        shapesManager,
        visibilityManager,
        measurement,
        drawing,
        pdfGhosting: {
          opacity: pdfGhosting.opacity,
          setOpacity: pdfGhosting.setOpacity,
        },
        ...activeSheetContext,
        ...selectDbIds,
        ...activeToolContext,
        showSheetShapeLabels,
        setShowSheetShapeLabels,
        colorization,
        propertyData,
        isLoading,
      }}
    >
      <>
        <Fade
          in={
            isLoading && !shouldHideViewer && viewerMode === ViewerMode.SheetsV2
          }
          unmountOnExit
        >
          <AbsoluteCenter axis="both" textAlign={'center'}>
            <Spinner color="brand.400" />
            <Text size="sm" color={loadingTextColor}>
              {t('viewer.loading-sheets')}
            </Text>
          </AbsoluteCenter>
        </Fade>

        {menuElement}
        {children}
        {sheetsViewerMode === ViewerMode.SheetsV2 && (
          <GridItem
            id={'sheets-viewer-v2'}
            position="relative"
            ref={viewerRef}
            zIndex={
              !shouldHideViewer && viewerMode === ViewerMode.SheetsV2 ? 1 : 0
            }
            opacity={
              !shouldHideViewer && viewerMode === ViewerMode.SheetsV2 ? 1 : 0
            }
            pointerEvents={shouldHideViewer ? 'none' : undefined}
            area="sidebar-left-start / sidebar-left-start / sidebar-right-end / sidebar-right-end"
            {...bindContextMenu()}
            sx={{
              '.adsk-viewing-viewer': {
                transition: 'opacity 0.8s ease-in-out',
                opacity: !isLoading ? 1 : 0,
                bottom: undefined,
              },
              '@media (max-width: 1200px)': {
                gridArea:
                  'sidebar-left-start / sidebar-left-start / main-end / main-end',
              },
            }}
          >
            {/* Show NoSheetsPlaceholder when there are no sheets */}
            {!isLoading &&
              activeSheetContext.sheets &&
              activeSheetContext.sheets.length === 0 &&
              !shouldHideViewer &&
              viewerMode === ViewerMode.SheetsV2 && (
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  width="100%"
                  height="100%"
                  zIndex={100}
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Image
                    alt="sheets placeholder"
                    objectFit="contain"
                    src={noSheetsPlaceholder}
                    maxHeight={300}
                  />
                  <Heading color="gray.500" size="lg" as="h3" mt={4}>
                    {t('viewer.no-sheets-placeholder')}
                  </Heading>
                  <PlaceholderText>
                    {t('viewer.no-sheets-description')}
                  </PlaceholderText>
                </Box>
              )}
          </GridItem>
        )}
      </>
    </SheetsViewerV2Context.Provider>
  );
}
